// DJSEM Live state model (v0.9): LivePlaybackState, device/deck/track state, package
// match state, conductor state, output frame, options. Pure types.

import type { DJSEMLiveEventEnvelope, DJSEMDeviceType } from "./protocol";

/** Derived health of the live bridge connection. */
export type ConnectionQuality = "excellent" | "good" | "degraded" | "lost" | "offline";
/** Overall derived mode of the live playback/conducting pipeline. */
export type ConductorMode =
  | "offline" | "waiting_for_bridge" | "waiting_for_devices" | "waiting_for_track"
  | "matching_package" | "armed" | "conducting" | "degraded" | "safe_mode" | "manual_override";

/** Normalized state of one discovered hardware device (CDJ/XDJ/mixer/etc.). */
export interface DJSEMLiveDeviceState {
  deviceId: string; playerNumber?: number; name?: string; ipAddress?: string;
  deviceType: DJSEMDeviceType; status: "online" | "lost" | "degraded" | "unknown";
  lastSeenAt: number; confidence: number;
}

/** Identity/metadata of the track currently loaded on a deck. */
export interface DJSEMLiveTrackState {
  liveTrackId?: string; rekordboxId?: string; title?: string; artist?: string;
  remixName?: string; sourceFilename?: string; durationSeconds?: number; bpm?: number;
  key?: string; loadedAt: number; confidence: number;
}

/** A deck's current transport/beat/tempo state. */
export interface DJSEMLiveDeckPlayback {
  isPlaying: boolean; positionSeconds: number; positionBeats?: number; barNumber?: number;
  beatInBar?: number; bpm?: number; effectiveBpm?: number; pitchPercent?: number;
  tempoMaster?: boolean; synced?: boolean; updatedAt: number;
}
/** A deck's current loop state. */
export interface DJSEMLiveDeckLoop { isLooping: boolean; loopStartSeconds?: number; loopEndSeconds?: number; loopLengthBeats?: number; }
/** Normalized state of one deck: its track, playback, and loop status. */
export interface DJSEMLiveDeckState {
  deckId: string; playerNumber?: number; deviceId?: string;
  status: "unknown" | "empty" | "loaded" | "playing" | "paused" | "cued" | "lost";
  track?: DJSEMLiveTrackState; playback: DJSEMLiveDeckPlayback; loop?: DJSEMLiveDeckLoop; confidence: number;
}

/** Outcome status of matching the loaded track to an approved package. */
export type PackageMatchStatus =
  | "not_started" | "matched" | "multiple_candidates" | "no_match"
  | "manual_selected" | "unapproved_package" | "fallback_demo_package"
  | "metadata_incomplete" | "manual_selection_required";
/** One scored candidate package considered during package matching. */
export interface DJSEMPackageMatchCandidate { packageId: string; trackId: string; title: string; artist?: string; packageVersion: string; approvedForLive: boolean; confidence: number; matchReasons: string[]; }
/** Result of matching the active live track against candidate packages. */
export interface DJSEMPackageMatchState { status: PackageMatchStatus; packageId?: string; trackId?: string; confidence: number; candidates: DJSEMPackageMatchCandidate[]; reason?: string; }

/** The full normalized live playback state: connection, devices, decks, and derived conductor mode. */
export interface LivePlaybackState {
  connection: { bridgeConnected: boolean; bridgeHealthy: boolean; lastHeartbeatAt?: number; networkOnline: boolean; connectionQuality: ConnectionQuality; warnings: string[] };
  devices: Record<string, DJSEMLiveDeviceState>;
  decks: Record<string, DJSEMLiveDeckState>;
  activeDeckId?: string;
  tempoMasterDeckId?: string;
  activeTrack?: DJSEMLiveTrackState;
  liveClock: { now: number; latencyMs: number; driftMs: number; lastBeatAt?: number; estimatedPositionSeconds?: number };
  packageMatch?: DJSEMPackageMatchState;
  activeShowCue?: { cueId: string; packageId: string; startTime: number; endTime: number; confidence: number };
  conductorMode: ConductorMode;
  recentEvents: DJSEMLiveEventEnvelope[];
  updatedAt: number;
}

/** Tuning options for {@link reduceLivePlaybackState} and {@link applyClockTick}. */
export interface LivePlaybackReducerOptions {
  now?: number; heartbeatTimeoutMs?: number; recentEventLimit?: number;
  cueJumpDetectionThresholdSeconds?: number;
}

/** Simulated visual mode used while the conductor is in safe mode. */
export type FallbackVisualMode = "blackout" | "ambient_idle" | "low_intensity_pulse" | "manual_hold";

// DJSEMOutputFrame's canonical home is @djsem/render-state (Prompt 01: render-state owns
// output frame contracts). Re-exported here for backwards compatibility: the conductor
// produces frames, so every existing `import { DJSEMOutputFrame } from "@djsem/live-core"`
// keeps working.
export type { DJSEMOutputFrame } from "@djsem/render-state";
import type { DJSEMOutputFrame } from "@djsem/render-state";

/** Overall mode of the (beat-link-oriented) Live Show Conductor. */
export type LiveConductorMode = "offline" | "waiting" | "armed" | "conducting" | "degraded" | "safe_mode" | "manual_override";
/** State produced by the beat-link Live Show Conductor (see conductor.ts's `buildLiveConductorState`). */
export interface DJSEMLiveConductorState {
  mode: LiveConductorMode;
  activeDeckId?: string; activeTrackId?: string; activePackageId?: string; activeCueId?: string;
  trackPositionSeconds?: number; correctedPositionSeconds?: number;
  beat?: { beatNumber?: number; beatInBar?: number; barNumber?: number; bpm?: number };
  packageMatch?: DJSEMPackageMatchState;
  renderState?: unknown; outputFrame?: DJSEMOutputFrame;
  safety: { safeModeActive: boolean; reason?: string; fallbackVisualMode: FallbackVisualMode };
  diagnostics: { latencyMs: number; driftMs: number; packageMatchConfidence: number; cueConfidence: number; bridgeConnected: boolean; lastEventType?: string; lastEventAt?: number; warnings: string[] };
}

/** Tuning options for `buildLiveConductorState` (conductor.ts). */
export interface LiveConductorOptions {
  latencyCompensationMs: number; maxDriftBeforeCorrectionMs: number;
  cueJumpDetectionThresholdSeconds: number; safeModeTimeoutMs: number;
  packageMatchThreshold: number; allowUnapprovedPackagesInDemoMode: boolean;
  allowRepeatedDropOverdriveInLoops: boolean; reducedIntensityWhenDegraded: boolean;
}

/** Default {@link LiveConductorOptions} used when the caller doesn't override them. */
export const DEFAULT_CONDUCTOR_OPTIONS: LiveConductorOptions = {
  latencyCompensationMs: 30, maxDriftBeforeCorrectionMs: 150, cueJumpDetectionThresholdSeconds: 1.0,
  safeModeTimeoutMs: 2000, packageMatchThreshold: 0.75, allowUnapprovedPackagesInDemoMode: false,
  allowRepeatedDropOverdriveInLoops: false, reducedIntensityWhenDegraded: true,
};
